// @ts-check
/**
 * Materials catalog — the shared reference list of fabrics, trims, and vendor
 * defaults used by both the D2C product BOM (Products → a variant → Materials)
 * and the atelier project BOM (Atelier → a project → Materials & Costing tab).
 *
 * This page only manages the catalog (what a material is, its default cost/vendor).
 * Actual quantities-per-garment are entered on the product/project itself.
 */

import { Edit2, Layers, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import adminMaterialService from '../services/adminMaterialService';
import vendorService from '../services/vendorService';

const CATEGORIES = [
  { value: 'fabric_shell', label: 'Fabric — Shell' },
  { value: 'fabric_lining', label: 'Fabric — Lining' },
  { value: 'trim_button', label: 'Trim — Button' },
  { value: 'trim_zipper', label: 'Trim — Zipper' },
  { value: 'thread', label: 'Thread' },
  { value: 'interfacing', label: 'Interfacing' },
  { value: 'label_tag', label: 'Label / Tag' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'embellishment', label: 'Embellishment' },
  { value: 'other', label: 'Other' },
];

const PROCESSES = [
  { value: '', label: 'None' },
  { value: 'raw', label: 'Raw' },
  { value: 'printed', label: 'Printed' },
  { value: 'dyed', label: 'Dyed' },
  { value: 'embroidered', label: 'Embroidered' },
  { value: 'hand_painted', label: 'Hand-painted' },
  { value: 'other', label: 'Other' },
];

const UNITS = ['meter', 'yard', 'piece', 'gram', 'roll', 'spool'];
const CURRENCIES = ['INR', 'USD', 'CNY', 'EUR', 'GBP'];

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const EMPTY_FORM = {
  category: 'fabric_shell',
  process: '',
  name: '',
  default_unit: 'meter',
  default_currency: 'INR',
  default_vendor_id: '',
  default_unit_cost: '',
  is_imported: false,
  default_customs_duty_percentage: '',
  default_freight_cost_per_unit: '',
  notes: '',
};

const MaterialsPage = () => {
  const toast = useToast();
  const [materials, setMaterials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Touched-since-open flag, reset by openCreate/openEdit (loading a form
  // never itself counts as an edit) and after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  const [form, setFormRaw] = useState(EMPTY_FORM);
  const setForm = useCallback((updater) => {
    setIsDirty(true);
    setFormRaw(updater);
  }, []);
  const [categoryFilter, setCategoryFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [materialData, vendorData] = await Promise.allSettled([
        adminMaterialService.getMaterials(categoryFilter ? { category: categoryFilter } : {}),
        vendorService.getVendors(),
      ]);
      if (materialData.status === 'fulfilled') setMaterials(materialData.value || []);
      else toast.error('Failed to load materials');
      if (vendorData.status === 'fulfilled') setVendors(vendorData.value || []);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setFormRaw(EMPTY_FORM);
    setIsDirty(false);
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setFormRaw({
      category: m.category,
      process: m.process || '',
      name: m.name || '',
      default_unit: m.default_unit,
      default_currency: m.default_currency || 'INR',
      default_vendor_id: m.default_vendor_id || '',
      default_unit_cost: m.default_unit_cost ?? '',
      is_imported: !!m.is_imported,
      default_customs_duty_percentage: m.default_customs_duty_percentage ?? '',
      default_freight_cost_per_unit: m.default_freight_cost_per_unit ?? '',
      notes: m.notes || '',
    });
    setIsDirty(false);
    setShowModal(true);
  };

  // X/Cancel dismiss the modal — confirm first if there's anything to lose.
  // A successful save closes directly (see handleSave), bypassing this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setShowModal(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Material name is required');
      return;
    }
    const payload = {
      ...form,
      process: form.process || null,
      default_vendor_id: form.default_vendor_id || null,
      default_unit_cost: form.default_unit_cost === '' ? null : Number(form.default_unit_cost),
      default_customs_duty_percentage:
        form.default_customs_duty_percentage === '' ? null : Number(form.default_customs_duty_percentage),
      default_freight_cost_per_unit:
        form.default_freight_cost_per_unit === '' ? null : Number(form.default_freight_cost_per_unit),
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await adminMaterialService.updateMaterial(editingId, payload);
        toast.success('Material updated');
      } else {
        await adminMaterialService.createMaterial(payload);
        toast.success('Material added');
      }
      setIsDirty(false);
      setShowModal(false);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to save material');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    try {
      await adminMaterialService.deleteMaterial(m.id);
      toast.success('Material removed from catalog');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete material');
    }
  };

  const vendorName = (id) => vendors.find((v) => v.id === id)?.vendor_name || '—';

  if (loading) return <LoadingScreen />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-serif flex items-center gap-2">
            <Layers size={24} />
            Materials
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Fabrics, trims, and their default vendor/cost. Actual quantities-per-garment are entered on the product
            variant or atelier project itself.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Material
          </button>
        </div>
      </div>

      <div className="mb-4">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg border-input text-sm"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {materials.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border rounded-lg border-border">
          No materials in the catalog yet.
        </div>
      ) : (
        <div className="bg-card border rounded-lg border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Category</th>
                <th className="text-left px-4 py-3 font-medium">Default Vendor</th>
                <th className="text-left px-4 py-3 font-medium">Default Cost</th>
                <th className="text-left px-4 py-3 font-medium">Imported</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m) => (
                <tr key={m.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-4 py-3">
                    <p className="font-medium">{m.name}</p>
                    {m.process && (
                      <p className="text-xs text-muted-foreground/70 capitalize">{m.process.replace(/_/g, ' ')}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">{CATEGORY_LABELS[m.category] || m.category}</td>
                  <td className="px-4 py-3">{vendorName(m.default_vendor_id)}</td>
                  <td className="px-4 py-3">
                    {m.default_unit_cost != null
                      ? `${m.default_currency} ${m.default_unit_cost} / ${m.default_unit}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {m.is_imported ? (
                      <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300">
                        Imported{' '}
                        {m.default_customs_duty_percentage ? `· ${m.default_customs_duty_percentage}% duty` : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/70">Domestic</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(m)}
                        className="p-2 text-muted-foreground hover:text-foreground"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(m)}
                        className="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal onClose={handleCloseClick} maxWidth="2xl" closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-serif">{editingId ? 'Edit Material' : 'Add Material'}</h2>
            <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
              <X size={22} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Mulberry Silk Georgette — Ivory"
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Process (fabric only)</label>
                <select
                  value={form.process}
                  onChange={(e) => setForm({ ...form, process: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                >
                  {PROCESSES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Default Unit</label>
                <select
                  value={form.default_unit}
                  onChange={(e) => setForm({ ...form, default_unit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                >
                  {UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Currency</label>
                <select
                  value={form.default_currency}
                  onChange={(e) => setForm({ ...form, default_currency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default Unit Cost</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.default_unit_cost}
                  onChange={(e) => setForm({ ...form, default_unit_cost: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Default Vendor</label>
              <select
                value={form.default_vendor_id}
                onChange={(e) => setForm({ ...form, default_vendor_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              >
                <option value="">— None —</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vendor_name} ({v.currency || 'INR'})
                  </option>
                ))}
              </select>
            </div>

            <div className="border rounded-lg border-border p-4 space-y-3 bg-muted/50">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.is_imported}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      is_imported: e.target.checked,
                      ...(e.target.checked
                        ? {}
                        : { default_customs_duty_percentage: '', default_freight_cost_per_unit: '' }),
                    })
                  }
                />
                Imported (customs duty + freight apply)
              </label>
              {form.is_imported && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Customs Duty %</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.default_customs_duty_percentage}
                      onChange={(e) => setForm({ ...form, default_customs_duty_percentage: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Freight Cost / Unit (INR)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.default_freight_cost_per_unit}
                      onChange={(e) => setForm({ ...form, default_freight_cost_per_unit: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex justify-end gap-2">
            <button
              onClick={handleCloseClick}
              disabled={saving}
              className="px-4 py-2 border rounded-lg border-input hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
            >
              {saving ? 'Saving...' : 'Save Material'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MaterialsPage;
